import Vue from 'vue'
import Router, { Route } from 'vue-router'
import PoliciesPage from '@/pages/PoliciesPage.vue'
import PostboxPage from '@/pages/PostboxPage.vue'
import NotFoundPage from '@/pages/NotFoundPage.vue'
import OverviewPage from '@/pages/OverviewPage.vue'
import ActivationPage from '@/pages/ActivationPage.vue'
import RegistrationPage from '@/pages/RegistrationPage.vue'
import SettingsPage from '@/pages/SettingsPage.vue'
import ChangeUsernamePage from '@/pages/ChangeUsernamePage.vue'
import ChangeEmailPage from '@/pages/ChangeEmailPage.vue'
import IdLevelUpgradePage from '@/pages/IdLevelUpgradePage.vue'
import PolicyDetailPage from '@/pages/PolicyDetailPage.vue'
import QuoteDetailPage from '@/pages/QuoteDetailPage.vue'
import CorrectEmailPage from '@/pages/CorrectEmailPage.vue'
import ResendActivationMailPage from '@/pages/ResendActivationMailPage.vue'
import ResetPasswordPage from '@/pages/ResetPasswordPage.vue'
import { Position } from 'vue-router/types/router'
import UsernameRetrievalPage from '@/pages/UsernameRetrievalPage.vue'
import ChangeVehiclePage from '@/pages/ChangeVehiclePage.vue'
import MotorContractTerminationPage from '@/pages/MotorContractTerminationPage.vue'
import PersonalDataPage from '@/pages/PersonalDataPage.vue'
import ChangeAddressPage from '@/pages/ChangeAddressPage.vue'
import ChangeNamePage from '@/pages/ChangeNamePage.vue'
import ChangeBankDataPage from '@/pages/ChangeBankDataPage.vue'
import VertragsServicesPage from '@/pages/VertragsServicesPage.vue'
import ServiceCodeRequestPage from '@/pages/ServiceCodeRequestPage.vue'
import ActivationExpiredPage from '@/pages/ActivationExpiredPage.vue'
import ChangeMotorCockpitPage from '@/pages/ChangeMotorCockpitPage.vue'
import ActivationSuccessfulPage from '@/pages/ActivationSuccessfulPage.vue'
import RegistrationSuccessfulPage from '@/pages/RegistrationSuccessfulPage.vue'
import { minValidityInSeconds, setSitecoreCookies } from '@/utils/KeycloakUtils'
import { setCpUserIdCookie } from '@/utils/CpUserIdUtils'
import { setRedPartnerStatusCookie } from '@/utilsTenant/RedCooperationUtils'
import ClaimsPage from '@/pagesTenant/ClaimsPage.vue'
import UserExistsPage from '@/pages/UserExistsPage.vue'
import ThankYouPage from '@/pages/ThankYouPage.vue'

let initialized = false

export default function (): void {
  if (initialized) {
    return
  }

  Vue.use(Router)
  const routes = [
    // public pages
    { path: '/', name: 'root', redirect: '/ueberblick' },
    { path: '/anmelden', name: 'anmelden', redirect: '/ueberblick' },
    { path: '/aktivierung', name: 'aktivierung', component: ActivationPage },
    { path: '/registrierung', redirect: '/registrieren' },
    { path: '/registrieren', name: 'registrierung', component: RegistrationPage },
    { path: '/registrieren-bestaetigung', name: 'registrieren-bestaetigung', component: RegistrationSuccessfulPage },
    { path: '/korrektur-email', name: 'korrektur-email', component: CorrectEmailPage },
    {
      path: '/neuanforderung_aktivierungsmail',
      name: 'neuanforderung_aktivierungsmail',
      component: ResendActivationMailPage
    },
    { path: '/passwort-neu-vergeben', name: 'passwort-neu-vergeben', component: ResetPasswordPage },
    { path: '/benutzername-vergessen', name: 'benutzername-vergessen', component: UsernameRetrievalPage },
    { path: '/aktivierung-abgelaufen', name: 'aktivierung-abgelaufen', component: ActivationExpiredPage },
    { path: '/aktivieren-bestaetigung', name: 'aktivieren-bestaetigung', component: ActivationSuccessfulPage },
    { path: '/registrieren-vorhanden', name: 'registrieren-vorhanden', component: UserExistsPage },
    { path: '*', component: NotFoundPage }, // fallback for any non matching route

    // secured pages
    {
      path: '/ueberblick',
      name: 'ueberblick',
      component: OverviewPage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: '/vertraege',
      name: 'vertraege',
      component: PoliciesPage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: '/vertraege/policen/:line/:partnerAddressId/:policyId',
      name: 'policen',
      component: PolicyDetailPage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: '/abschluss/:quoteId',
      name: 'quotes',
      component: QuoteDetailPage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: '/postfach',
      name: 'postfach',
      component: PostboxPage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: '/schaeden',
      name: 'schaeden',
      component: ClaimsPage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: '/einstellungen',
      name: 'einstellungen',
      component: SettingsPage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: '/persoenliche-daten',
      name: 'personalData',
      component: PersonalDataPage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: '/persoenliche-daten/adresse-aendern',
      name: 'changeAddress',
      component: ChangeAddressPage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: '/persoenliche-daten/name-aendern',
      name: 'changeName',
      component: ChangeNamePage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: '/persoenliche-daten/bankdaten-aendern',
      name: 'changeBankData',
      component: ChangeBankDataPage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: '/einstellungen/benutzername',
      name: 'benutzername',
      component: ChangeUsernamePage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: '/einstellungen/passwort',
      name: 'passwort',
      component: ResetPasswordPage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: '/einstellungen/email',
      name: 'email',
      component: ChangeEmailPage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: window.ENV_CONFIG.SERVICE_LINKS.SETTINGS_PAGE.CHANGE_SERVICECODEINPUT_PAGE,
      name: 'servicecodeEingeben',
      component: IdLevelUpgradePage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: window.ENV_CONFIG.SERVICE_LINKS.SETTINGS_PAGE.CHANGE_SERVICECODEREQUEST_PAGE,
      name: 'servicecodeAnfordern',
      component: ServiceCodeRequestPage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: '/vertraege/policen/motor/:partnerAddressId/:policyId/vertrag-kuendigen',
      name: 'vertragKuendigen',
      component: MotorContractTerminationPage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: window.ENV_CONFIG.SERVICE_LINKS.VERTRAGSSERVICES_PAGE.ROOT,
      name: 'vertragsServices',
      component: VertragsServicesPage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: window.ENV_CONFIG.SERVICE_LINKS.VERTRAGSSERVICES_PAGE.ROOT + '/motor-cockpit-fahrleistung',
      name: 'motorCockpitFahrleistung',
      component: ChangeMotorCockpitPage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: window.ENV_CONFIG.SERVICE_LINKS.VERTRAGSSERVICES_PAGE.ROOT + '/motor-cockpit-versicherungsumfang',
      name: 'motorCockpitVersicherungsumfang',
      component: ChangeMotorCockpitPage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: window.ENV_CONFIG.SERVICE_LINKS.VERTRAGSSERVICES_PAGE.ROOT + '/motor-cockpit-fahrerkreis',
      name: 'motorCockpitFahrerkreis',
      component: ChangeMotorCockpitPage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: '/vertraege/policen/motor/:partnerAddressId/:policyId/fahrzeug-wechseln',
      name: 'fahrzeugWechseln',
      component: ChangeVehiclePage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    },
    {
      path: '/danke',
      name: 'danke',
      component: ThankYouPage,
      beforeEnter: (to: Route, from: Route, next: () => void) => verifyUserIsLoggedIn(to, next)
    }
  ]

  const router = new Router({
    mode: 'history',
    base: window.ENV_CONFIG.BASE_URL || '/',
    routes,
    scrollBehavior: (_to: Route, _from: Route, _savedPosition: Position | void) => {
      // scroll to top
      return { x: 0, y: 0 }
    }
  })

  if (process.env.VUE_APP_MULTI_PAGE_APP_BEHAVIOUR === 'true') {
    // use window.location.href when navigating to a different url
    // in order to ensure a full page refresh like in a multi page app
    // also, the web-component-loader.js will then correctly re-register an already registered web component
    // when a script tag is re-added in the header via the ScriptLoader.ts
    router.beforeEach((to: Route, from: Route, next: () => void) => {
      if (isPathOnSamePage(to.fullPath)) {
        next()
        return
      }

      reloadPage(to)
      return
    })
  }

  Vue.mixin({ router })

  initialized = true

  function isPathOnSamePage(path: string): boolean {
    return window.location.href.endsWith(path)
  }

  function reloadPage(targetRoute: Route) {
    if (router.mode === 'hash') {
      window.location.href = '/#' + targetRoute.fullPath
      // due to the hash url, we are staying on the same page
      // and thus need a window.location.reload to enforce a page reload
      // if entering a wrong route not containing any #, vue router appends a #/ at the end
      // in that case a redirect to the start page happens and we do not force a page reload
      // in order to prevent an endless loop
      if (!window.location.href.endsWith('#/')) {
        window.location.reload()
      }
    } else {
      window.location.href = targetRoute.fullPath
    }
  }

  function verifyUserIsLoggedIn(to: Route, next: () => void) {
    if (window.keycloak.authenticated) {
      window.keycloak
        .updateToken(minValidityInSeconds)
        .then(async (_isRefreshed) => {
          setSitecoreCookies(window.keycloak.refreshToken)
          setCpUserIdCookie()
          await setRedPartnerStatusCookie()
          next()
        })
        .catch(() => {
          console.log('update token not successful')
          window.location.assign(window.ENV_CONFIG.REDIRECTS.LOGOUT_REDIRECT)
        })
    } else {
      window.keycloak.login()
    }
  }
}
